import { GeoJSON } from 'ol/format';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat } from 'ol/proj';
import ImageCanvasSource from 'ol/source/ImageCanvas';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { MapLayer } from '../MapLayer';
import { IdwField } from './IdwField';
import { IdwRaster } from './IdwRaster';
/**
 * Abstract per-metric vector layer.
 *
 * Each concrete subclass declares:
 *   - a stable layer name + LayerSwitcher title,
 *   - a {@link MetricColorScale} that maps the metric's value range
 *     to a colour ramp (subclasses can re-tune the scale by passing a
 *     different ramp or range to the constructor).
 *
 * The base class handles the rest: source/layer construction, GeoJSON
 * pipeline, and the per-feature style (filled circle, colour from
 * the scale).
 */
export class MetricLayer extends MapLayer {
    constructor(scale) {
        super();
        /**
         * @protected
         */
        this._points = [];
        /**
         * Both metric layers start hidden so the page can show the plain
         * sighting layer first; the user opts in via the LayerSwitcher.
         * @protected
         */
        this._initiallyVisible = false;
        /**
         * @protected
         */
        this._mode = 'points';
        /**
         * @protected
         */
        this._changeListeners = new Set();
        this._scale = scale;
    }
    getOlLayer() {
        if (!this._layer) {
            this._source = new VectorSource({ wrapX: false });
            this._layer = this._buildOlLayer();
        }
        return this._layer;
    }
    setPoints(points) {
        this._points = points;
    }
    clear() {
        var _a;
        this._points = [];
        (_a = this._source) === null || _a === void 0 ? void 0 : _a.clear(true);
    }
    getScale() {
        return this._scale;
    }
    getMode() {
        return this._mode;
    }
    /**
     * Switch between point and IDW-interpolation rendering. Replaces
     * the underlying OL layer in place — the two modes use different
     * layer types (VectorLayer vs ImageLayer), so swapping the OL
     * layer is the cleanest path. The shared {@link _source} is
     * preserved across the swap (points mode still reads from it).
     *
     * Fires every registered {@link onChange} callback after the swap.
     */
    setMode(mode) {
        if (mode === this._mode) {
            return;
        }
        this._mode = mode;
        this._idwSource = undefined;
        if (!this._layer) {
            // Layer not built yet — mode will be honoured at first getOlLayer().
            this._emitChange();
            return;
        }
        const base = this._base;
        const olMap = base === null || base === void 0 ? void 0 : base.getOlMap();
        if (!base || !olMap) {
            return;
        }
        const wasVisible = this._layer.getVisible();
        olMap.removeLayer(this._layer);
        this._layer = this._buildOlLayer();
        this._layer.set('name', this.getName());
        this._layer.set('title', this.getTitle());
        this._layer.setVisible(wasVisible);
        olMap.addLayer(this._layer);
        base.refreshLayerSwitcher();
        this._emitChange();
    }
    isVisible() {
        var _a, _b;
        return (_b = (_a = this._layer) === null || _a === void 0 ? void 0 : _a.getVisible()) !== null && _b !== void 0 ? _b : this._initiallyVisible;
    }
    /**
     * Register a listener fired whenever the layer's visibility
     * or render mode changes. Returns an unsubscribe function.
     */
    onChange(cb) {
        this._changeListeners.add(cb);
        return () => {
            this._changeListeners.delete(cb);
        };
    }
    /**
     * Commit the buffered points to the layer's source. Replaces what
     * was rendered before.
     */
    refresh() {
        var _a;
        if (!this._source) {
            return;
        }
        this._source.clear(true);
        if (this._points.length === 0) {
            return;
        }
        const features = this._points.map((p) => ({
            type: 'Feature',
            properties: {
                id: p.id,
                metric_value: p.value,
                content: p.content
            },
            geometry: {
                type: 'Point',
                coordinates: [p.lon, p.lat]
            }
        }));
        const collection = {
            type: 'FeatureCollection',
            crs: { type: 'name', properties: { name: 'EPSG:4326' } },
            features
        };
        const olFeatures = new GeoJSON().readFeatures(collection, {
            featureProjection: 'EPSG:3857'
        });
        this._source.addFeatures(olFeatures);
        /*
         * Invalidate the IDW canvas so a re-render picks up the new
         * points on the next pan/zoom (or immediately, if the layer
         * is currently visible).
         */
        (_a = this._idwSource) === null || _a === void 0 ? void 0 : _a.changed();
    }
    /**
     * @protected
     */
    _styleFor(feature) {
        const value = feature.get('metric_value');
        const color = this._scale.colorFor(value);
        return [new Style({
                image: new Circle({
                    radius: 7,
                    fill: new Fill({ color }),
                    stroke: new Stroke({ color: 'rgba(0,0,0,0.55)', width: 1 })
                })
            })];
    }
    /**
     * Build a fresh OL layer for the current {@link _mode}, wiring the
     * visibility-change listener so external observers can react.
     *
     * IDW (interpolation) layers render below points (z=85 vs z=90)
     * so that a stacked combination of "metric A interpolated +
     * metric B as points" keeps the sighting positions visible on top
     * of the interpolated surface — matches the typical reading order
     * (background context + foreground markers).
     * @protected
     */
    _buildOlLayer() {
        const layer = this._mode === 'idw'
            ? this._buildIdwLayer()
            : this._buildPointsLayer();
        layer.setZIndex(this._mode === 'idw' ? 85 : 90);
        layer.on('change:visible', () => this._emitChange());
        return layer;
    }
    /**
     * @protected
     */
    _buildPointsLayer() {
        return new VectorLayer({
            source: this._source,
            visible: this._initiallyVisible,
            style: (feature) => this._styleFor(feature)
        });
    }
    /**
     * Inverse-distance-weighted interpolation surface.
     *
     * For each pixel in the requested extent, looks up the K nearest
     * sightings within {@link _IDW_RADIUS_M} metres and computes the
     * weighted average of their metric values using `1 / d^2` weights.
     * Pixels with no sighting inside the radius stay transparent so
     * the user sees gaps where the data doesn't support extrapolation,
     * rather than a misleadingly-coloured ocean.
     *
     * Sampling step ({@link _IDW_SAMPLE_STEP}) trades resolution for
     * speed — each computed pixel paints a step×step block, so a step
     * of 4 cuts the work 16-fold at the cost of a blocky surface
     * (acceptable for an analytic overlay).
     *
     * OL caches the resulting canvas per (extent, resolution), so the
     * cost is paid once per pan/zoom, not per frame.
     * @protected
     */
    _buildIdwLayer() {
        const source = new ImageCanvasSource({
            ratio: 1,
            canvasFunction: (extent, _resolution, _pixelRatio, size) => {
                return this._paintIdwCanvas(extent, size);
            }
        });
        this._idwSource = source;
        return new ImageLayer({
            source,
            visible: this._initiallyVisible
        });
    }
    /**
     * @protected
     */
    _paintIdwCanvas(extent, size) {
        const w = Math.max(1, Math.round(size[0]));
        const h = Math.max(1, Math.round(size[1]));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return canvas;
        }
        /*
         * Project points to the map projection (EPSG:3857) once per
         * paint. Drop null/NaN values — they have no contribution to
         * the weighted average.
         */
        const projected = [];
        for (const p of this._points) {
            if (p.value !== null && Number.isFinite(p.value)) {
                const [x, y] = fromLonLat([p.lon, p.lat]);
                projected.push({ x, y, v: p.value });
            }
        }
        if (projected.length === 0) {
            return canvas;
        }
        const imageData = ctx.createImageData(w, h);
        const data = imageData.data;
        const xMin = extent[0];
        const xMax = extent[2];
        const yMax = extent[3];
        const xSpan = xMax - xMin;
        const ySpan = yMax - extent[1];
        const radiusSq = MetricLayer._IDW_RADIUS_M * MetricLayer._IDW_RADIUS_M;
        const step = MetricLayer._IDW_SAMPLE_STEP;
        const field = new IdwField(projected, radiusSq, MetricLayer._IDW_NEIGHBOURS);
        const block = { w, h, px: 0, py: 0, step };
        for (let py = 0; py < h; py += step) {
            // EPSG:3857 north is +y, canvas down is +py.
            const wy = yMax - (((py + 0.5) / h) * ySpan);
            for (let px = 0; px < w; px += step) {
                const wx = xMin + (((px + 0.5) / w) * xSpan);
                const value = field.valueAt(wx, wy);
                if (value !== null) {
                    block.px = px;
                    block.py = py;
                    IdwRaster.fillBlock(data, block, this._scale.colorForRgba(value, 0.7));
                }
            }
        }
        ctx.putImageData(imageData, 0, 0);
        return canvas;
    }
    /**
     * @protected
     */
    _emitChange() {
        for (const cb of this._changeListeners) {
            cb();
        }
    }
}
/**
 * Search radius for the IDW renderer (metres). Points beyond this
 * distance from the pixel are ignored — so sparsely-sampled areas
 * stay transparent instead of being filled with extrapolated
 * values. 30 km is generous enough to bridge typical inter-track
 * spacing but small enough that an isolated track doesn't paint
 * half the map.
 * @protected
 */
MetricLayer._IDW_RADIUS_M = 30000;
/**
 * Max number of neighbouring sightings averaged per pixel.
 * @protected
 */
MetricLayer._IDW_NEIGHBOURS = 8;
/**
 * Pixel sampling step for the IDW canvas. Each computed pixel
 * paints a step×step block — at step=4 the work is cut 16× at
 * the cost of a slightly blocky surface.
 * @protected
 */
MetricLayer._IDW_SAMPLE_STEP = 4;
